// ##### Trip Data Sanitization and Hierarchical Transformation
// Functions to transform raw backend data into hierarchical structure for dynamic table

import { calculateDuration } from 'shared_items/formatConventions';

// ##### Country to Region Mapping
let countryRegionMap = {};
let backendResponded = false;
let isInitializing = false; // Prevent multiple simultaneous calls
let initializationPromise = null; // Cache the promise

// ##### Initialize country regions mapping from backend
export const initializeCountryRegions = async () => {
  // If already initialized, return immediately
  if (backendResponded && Object.keys(countryRegionMap).length > 0) {
    return;
  }
  
  // If currently initializing, wait for the existing promise
  if (isInitializing && initializationPromise) {
    return initializationPromise;
  }
  
  // If not initialized, start initialization
  if (!isInitializing) {
    isInitializing = true;
    initializationPromise = performInitialization();
  }
  
  return initializationPromise;
};

// ##### Perform the actual initialization
const performInitialization = async () => {
  try {
    const { getCountryRegions } = await import('./travel_services');
    countryRegionMap = await getCountryRegions();
    backendResponded = true;
    console.log('Country regions loaded from backend:', Object.keys(countryRegionMap).length, 'countries with ISO structure');
  } catch (error) {
    console.warn('Failed to load country regions from backend, using fallback');
    countryRegionMap = {};
    backendResponded = false;
  } finally {
    isInitializing = false;
  }
};

// ##### Get complete country info with proper fallback logic
const getCountryInfo = (country) => {
  if (!country) {
    return {
      region: 'Other',
      iso2: null,
      iso3: null,
      name: country,
      found: false
    };
  }
  
  if (!backendResponded) {
    return {
      region: 'Other - no connection',
      iso2: null,
      iso3: null,
      name: country,
      found: false
    };
  }
  
  // Use findCountryData with name pattern for complete country lookup
  const countryInfo = findCountryData(country, 'iso3');
  if (countryInfo) {
    return {
      region: countryInfo.region,
      iso2: countryInfo.iso2,
      iso3: countryInfo.iso3,
      name: countryInfo.name,
      found: true
    };
  }
  
  // Country not found in mapping, use "Other"
  return {
    region: 'Other',
    iso2: null,
    iso3: null,
    name: country,
    found: false
  };
};

// ##### Transform raw backend data to hierarchical structure
export const transformToHierarchicalData = (trips) => {
  if (!trips || !Array.isArray(trips)) {
    return [];
  }

  const regionMap = new Map();
  
  trips.forEach(trip => {
    // Skip trips without places
    if (!trip.places || !Array.isArray(trip.places) || trip.places.length === 0) {
      return;
    }
    
    trip.places.forEach(place => {
      // Determine region based on country
      const countryInfo = getCountryInfo(place.country);
      const region = countryInfo.region;
      
      // Initialize region if not exists
      if (!regionMap.has(region)) {
        regionMap.set(region, {
          region,
          countries: new Map(),
          _aggregated: { uniqueCountries: 0 }
        });
      }
      
      const regionData = regionMap.get(region);
      
      // Initialize country if not exists
      if (!regionData.countries.has(place.country)) {
        regionData.countries.set(place.country, {
          country: countryInfo.name || place.country,
          countryInfo: countryInfo, // Store countryInfo to avoid duplicate calls
          trips: [],
          _aggregated: { uniqueCountries: 1 }
        });
      }
      
      const countryData = regionData.countries.get(place.country);
      
      // Update countryInfo if it wasn't stored before
      if (!countryData.countryInfo) {
        countryData.countryInfo = countryInfo;
      }
      
      // Add trip information
      countryData.trips.push({
        tripName: trip.title,
        startDate: trip.start_date,
        endDate: trip.end_date,
        duration: calculateDuration(trip.start_date, trip.end_date),
        description: trip.description || '',
        googlePhotosUrl: trip.google_photos_url || '',
        placeName: place.name,
        placeArrivalDate: place.arrival_date,
        placeDepartureDate: place.departure_date,
        latitude: place.latitude,
        longitude: place.longitude,
        googlePlaceId: place.google_place_id,
        googleMapsUrl: place.google_maps_url
      });
    });
  });
  
  // Calculate aggregations
  regionMap.forEach(regionData => {
    regionData._aggregated.uniqueCountries = regionData.countries.size;
    regionData._aggregated.tripCount = 0;
    
    regionData.countries.forEach(countryData => {
      countryData._aggregated.uniqueCountries = 1;
      countryData._aggregated.tripCount = countryData.trips.length;
      regionData._aggregated.tripCount += countryData.trips.length;
    });
  });
  
  // Convert to array format for TanStack Table
  const hierarchicalData = [];
  
  regionMap.forEach(regionData => {
    // Add region level
    hierarchicalData.push({
      id: `region_${regionData.region}`,
      region: regionData.region,
      country: null,
      tripName: null,
      startDate: null,
      endDate: null,
      duration: null,
      uniqueCountries: regionData._aggregated.uniqueCountries,
      tripCount: regionData._aggregated.tripCount,
      level: 'region',
      isGrouped: true
    });
    
    regionData.countries.forEach(countryData => {
      // Add country level
      hierarchicalData.push({
        id: `country_${regionData.region}_${countryData.country}`,
        region: regionData.region,
        country: countryData.country,
        countryCode: backendResponded ? countryData.countryInfo.iso2 || null : null,
        tripName: null,
        startDate: null,
        endDate: null,
        duration: null,
        uniqueCountries: countryData._aggregated.uniqueCountries,
        tripCount: countryData._aggregated.tripCount,
        level: 'country',
        isGrouped: true
      });
      
      // Add trip level - sort places alphabetically by placeName
      const sortedTrips = [...countryData.trips].sort((a, b) => {
        const nameA = (a.placeName || '').toLowerCase();
        const nameB = (b.placeName || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
      
      sortedTrips.forEach((trip, index) => {
        hierarchicalData.push({
          id: `trip_${regionData.region}_${countryData.country}_${index}`,
          region: regionData.region,
          country: countryData.country,
          tripName: trip.tripName,
          startDate: trip.startDate,
          endDate: trip.endDate,
          duration: trip.duration,
          description: trip.description,
          googlePhotosUrl: trip.googlePhotosUrl,
          placeName: trip.placeName,
          placeArrivalDate: trip.placeArrivalDate,
          placeDepartureDate: trip.placeDepartureDate,
          latitude: trip.latitude,
          longitude: trip.longitude,
          googlePlaceId: trip.googlePlaceId,
          googleMapsUrl: trip.googleMapsUrl,
          uniqueCountries: 1,
          tripCount: 1,
          level: 'trip',
          isGrouped: false
        });
      });
    });
  });
  
  return hierarchicalData;
};

// ##### Transform trips data to hierarchical structure by year
export const transformToYearHierarchicalData = (trips) => {
  if (!trips || !Array.isArray(trips)) {
    return [];
  }

  // Group trips by year, then by trip
  const yearMap = new Map();
  
  trips.forEach(trip => {
    // Skip trips without start_date
    if (!trip.start_date) {
      return;
    }
    
    const year = new Date(trip.start_date).getFullYear();
    
    // Initialize year if not exists
    if (!yearMap.has(year)) {
      yearMap.set(year, new Map());
    }
    
    const tripsInYear = yearMap.get(year);
    
    // Initialize trip if not exists
    if (!tripsInYear.has(trip.id)) {
      tripsInYear.set(trip.id, {
        tripId: trip.id,
        tripName: trip.title,
        startDate: trip.start_date,
        endDate: trip.end_date,
        description: trip.description || '',
        googlePhotosUrl: trip.google_photos_url || '',
        places: []
      });
    }
    
    const tripData = tripsInYear.get(trip.id);
    
    // Add places to trip
    if (trip.places && Array.isArray(trip.places) && trip.places.length > 0) {
      trip.places.forEach(place => {
        tripData.places.push({
          placeName: place.name,
          placeCountry: place.country,
          placeArrivalDate: place.arrival_date,
          placeDepartureDate: place.departure_date,
          latitude: place.latitude,
          longitude: place.longitude,
          googlePlaceId: place.google_place_id,
          googleMapsUrl: place.google_maps_url
        });
      });
    }
  });
  
  // Convert to array format, sorted by year descending
  const hierarchicalData = [];
  const sortedYears = Array.from(yearMap.keys()).sort((a, b) => b - a); // Descending order
  
  sortedYears.forEach(year => {
    const tripsInYear = yearMap.get(year);
    
    // Add year level
    hierarchicalData.push({
      id: `year_${year}`,
      year: year,
      tripCount: tripsInYear.size,
      level: 'year',
      isGrouped: true
    });
    
    // Sort trips by startDate descending (newest first)
    const sortedTrips = Array.from(tripsInYear.entries()).sort((a, b) => {
      const dateA = new Date(a[1].startDate);
      const dateB = new Date(b[1].startDate);
      return dateB - dateA; // Descending order
    });
    
    // Add trip level and place level
    sortedTrips.forEach(([tripId, tripData]) => {
      // Add trip level
      hierarchicalData.push({
        id: `trip_${year}_${tripId}`,
        year: year,
        tripId: tripId,
        tripName: tripData.tripName,
        startDate: tripData.startDate,
        endDate: tripData.endDate,
        description: tripData.description,
        googlePhotosUrl: tripData.googlePhotosUrl,
        placeCount: tripData.places.length,
        level: 'trip',
        isGrouped: true
      });
      
      // Add place level
      tripData.places.forEach((place, placeIndex) => {
        hierarchicalData.push({
          id: `place_${year}_${tripId}_${placeIndex}`,
          year: year,
          tripId: tripId,
          tripName: tripData.tripName,
          startDate: tripData.startDate,
          endDate: tripData.endDate,
          description: tripData.description,
          googlePhotosUrl: tripData.googlePhotosUrl,
          placeName: place.placeName,
          placeCountry: place.placeCountry,
          placeArrivalDate: place.placeArrivalDate,
          placeDepartureDate: place.placeDepartureDate,
          latitude: place.latitude,
          longitude: place.longitude,
          googlePlaceId: place.googlePlaceId,
          googleMapsUrl: place.googleMapsUrl,
          level: 'place',
          isGrouped: false
        });
      });
    });
  });
  
  return hierarchicalData;
};

// ##### Get unique countries count for a specific level
export const getUniqueCountriesCount = (data, level) => {
  if (level === 'region') {
    return data.filter(item => item.level === 'country').length;
  } else if (level === 'country') {
    return 1;
  }
  return 0;
};

// ##### Utility function to find country data by different criteria
export const findCountryData = (searchTerm, searchPattern = 'iso3') => {
  if (!searchTerm || !backendResponded) return null;
  
  // Search by ISO2 code
  if (searchPattern === 'iso2' && countryRegionMap[searchTerm]) {
    return {
      iso2: countryRegionMap[searchTerm][0],
      iso3: countryRegionMap[searchTerm][1],
      name: countryRegionMap[searchTerm][2],
      region: countryRegionMap[searchTerm][3]
    };
  }
  
  // Search by ISO3 code
  if (searchPattern === 'iso3') {
    for (const [, countryData] of Object.entries(countryRegionMap)) {
      if (countryData[1] === searchTerm) {
        return {
          iso2: countryData[0],
          iso3: countryData[1],
          name: countryData[2],
          region: countryData[3]
        };
      }
    }
  }
  
  // Search by country name
  if (searchPattern === 'name') {
    for (const [, countryData] of Object.entries(countryRegionMap)) {
      if (countryData[2] === searchTerm) {
        return {
          iso2: countryData[0],
          iso3: countryData[1],
          name: countryData[2],
          region: countryData[3]
        };
      }
    }
  }
  
  return null;
};

/**
 * Build grouped country options for select UI (after initializeCountryRegions).
 * Each group: { region, countries: [{ iso2, iso3, name, region }] } sorted by region then ISO3.
 */
export const getCountrySelectOptionsGrouped = () => {
  if (!backendResponded || Object.keys(countryRegionMap).length === 0) {
    return [];
  }
  const byRegion = new Map();
  for (const [, row] of Object.entries(countryRegionMap)) {
    const iso2 = row[0];
    const iso3 = row[1];
    const name = row[2];
    const region = row[3];
    if (!byRegion.has(region)) {
      byRegion.set(region, []);
    }
    byRegion.get(region).push({ iso2, iso3, name, region });
  }
  return Array.from(byRegion.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([region, countries]) => ({
      region,
      countries: countries.sort((x, y) => x.iso3.localeCompare(y.iso3)),
    }));
};

// ##### Export mapping function for external use
export { getCountryInfo };
